import React, { Ref, useEffect, useState } from 'react';

import { observer } from 'mobx-react';
import { DetailColumn, loadRouteGroups } from '@bringg-frontend/monitoring-shared';
import classNames from 'classnames';
import { GroupData } from '@bringg/types';
import { List } from 'react-virtualized';
import { RenderedRows } from 'react-virtualized/dist/es/List';
import { getRootEnv } from '@bringg-frontend/bringg-web-infra';
import { useStores } from 'bringg-web';

import RouteIdentifier from './cells-renderers/routes-identifier/route-identifier';
import useRouteMonitoringStore from '../stores/helpers/use-route-monitoring-store';
import { getRowsCountWithBottomExtraSpace } from '../utils/rows-provider';
import { TableItem } from 'bringg-web/features/monitoring/shared/stores/groups/types';
import GroupRow from 'bringg-web/features/monitoring/routes-monitoring-screen/monitoring-table/cells-renderers/routes-identifier/group-row/group-row';
import useBulkActionsList from 'bringg-web/features/monitoring/shared/hooks/use-bulk-actions';
import MainColumnHeader from 'bringg-web/features/monitoring/shared/components/main-column-header/main-column-header';

import styles from './monitoring-table.module.scss';

export const ROUTES_LIST_ID = 'routes-list';

const customStyles = { header: styles.routesHeader, group: styles.groupRow };

interface Props {
	registerChild: Ref<List>;
	onRowsRendered: (info: RenderedRows) => void;
	withScroll?: boolean;
}

const MonitoringTable = ({ onRowsRendered, registerChild, withScroll }: Props) => {
	const { routesMonitoringStore, routesMonitoringViewStore, routesMonitoringTableViewStore } =
		useRouteMonitoringStore();
	const { runStore } = useStores();

	const [selectedTasksForBulkAction, setSelectedTasksForBulkAction] = useState([]);
	const { currentTableDefinition } = routesMonitoringStore;
	const { flatTableItems, total } = currentTableDefinition;

	const isGroupRow = (_row: TableItem): boolean => {
		return (_row as GroupData).group_type !== 'route';
	};

	useEffect(() => {
		const fetchTasksForBulkActions = async () => {
			const checkRoutes = runStore.getByIds(routesMonitoringTableViewStore.checkedRoutes);
			const routesTasks = await loadRouteGroups(
				routesMonitoringTableViewStore?.checkedRoutes,
				checkRoutes[0].team_id
			);
			const tasksIds = Object.values(routesTasks.items_by_group)
				.flat()
				.map(task => task.id);

			const tasks = getRootEnv().dashboardSdk.sdk.v2.tasks.getMany(tasksIds);
			setSelectedTasksForBulkAction(tasks);
		};
		if (routesMonitoringTableViewStore.checkedRoutes.length) {
			fetchTasksForBulkActions();
		} else {
			setSelectedTasksForBulkAction([]);
		}
	}, [routesMonitoringTableViewStore.checkedRoutes]);

	const actionsList = useBulkActionsList(selectedTasksForBulkAction);

	const mainColumn = {
		id: ROUTES_LIST_ID,
		Header: <MainColumnHeader bulkActionItems={actionsList} totalViewItems={0} onEyeIconClick={() => null} />,
		Cell: RouteIdentifier,
		Group: GroupRow
	};

	return (
		<div className={styles.container}>
			<DetailColumn<GroupData, GroupData>
				listRef={registerChild}
				onRowsRendered={onRowsRendered}
				withScrollBar={withScroll}
				rowData={flatTableItems as GroupData[]}
				rowCount={getRowsCountWithBottomExtraSpace(total)}
				column={mainColumn}
				customStyles={customStyles}
				idPrefix="routes-detailed-list-"
				data-test-id="monitoring-table"
				isGroupFn={isGroupRow}
			/>
			<div className={classNames(styles.shadow, { [styles.scroll]: routesMonitoringViewStore.isScrolled })} />
		</div>
	);
};

export default observer(MonitoringTable);
